'use client'

import type {
  EditionsCount,
  Filters,
  Newsletter,
  Newsletters,
} from '@snowball-tech/common/types'

import DisplayError from '@snowball-tech/common/components/DisplayError'
import EditionCard from '@snowball-tech/common/components/Edition/EditionCard'
import { toast } from '@snowball-tech/common/components/Toaster'
import { EditionStatuses, PublicationModes } from '@snowball-tech/common/enums'
import {
  usePathname,
  useRouter,
} from '@snowball-tech/common/helpers/navigation'
import useFeatureFlags from '@snowball-tech/common/hooks/useFeatureFlags'
import useLogger from '@snowball-tech/common/hooks/useLogger'
import useMediaQuery from '@snowball-tech/common/hooks/useMediaQuery'
import useTranslations from '@snowball-tech/common/hooks/useTranslations'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { Loader } from '@snowball-tech/fractal/dist/src/components/Loader'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilPlus as PlusIcon } from '@tooni/iconscout-unicons-react'
import { useSearchParams } from 'next/navigation'

import { useCallback, useRef, useState } from 'react'
import { useAsync } from 'react-async-hook'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'
import isNumber from 'lodash/fp/isNumber'
import omit from 'lodash/fp/omit'

import type { Editions, User } from '@/types'

import useApi from '@/hooks/useApi'

import MobileSearch from '../MobileSearch'
import SearchInput from '../SearchInput'
import NewsletterTagsList from './NewslettersTagsList'

type Props = {
  editions: Editions
  user: User | null
  centered?: boolean
  className?: string
  condensedCards?: boolean
  count?: EditionsCount
  editionCardClassNames?: string
  limit?: number
  newsletter?: Newsletter
  newsletters?: Newsletters
  nextPage?: string | null
  orientation?: 'auto' | 'horizontal' | 'vertical'
  refresh?: boolean
  smallPreview?: boolean
  variant?: 'bento' | 'cards' | 'list'
  withCount?: boolean
  withNewsletterTag?: boolean
  withPriceTag?: boolean
  withSearch?: boolean
}
export default function EditionsList({
  centered = true,
  className,
  condensedCards = false,
  count: initialCount = {},
  editionCardClassNames,
  editions: initialEditionsList = [],
  limit,
  newsletter,
  newsletters = [],
  nextPage: initialNextPage = '',
  orientation = 'auto',
  refresh = true,
  smallPreview = false,
  user,
  variant = 'list',
  withCount = true,
  withNewsletterTag,
  withPriceTag,
  withSearch = true,
}: Props) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const $t = useTranslations()
  const mediaQueries = useMediaQuery()

  const { editionsListPageSize } = useFeatureFlags()

  const endpoints = useApi()

  const logger = useLogger()

  const [filters, setFilters] = useState<{ search?: string } & Filters>({
    authorsUuids: searchParams.getAll('authors') ?? undefined,
    newslettersUuids: isEmpty(newsletter)
      ? (searchParams.getAll('newsletters') ?? undefined)
      : [newsletter.uuid],
  })
  const [search, setSearch] = useState(searchParams.get('query') ?? '')

  const lastSearch = useRef(search ?? '')
  const [retriggerSearch, setRetriggerSearch] = useState(0)
  const triggerSearch = (newSearch?: string, force = false) => {
    const valueToSearch = newSearch ?? search ?? ''
    if (!force && valueToSearch === lastSearch.current) {
      return
    }

    lastSearch.current = valueToSearch

    setFilters({
      ...filters,
      search: valueToSearch,
    })

    setRetriggerSearch((previous) => previous + 1)
  }

  const handleFiltersChange = (newFilters: Filters) => {
    const updatedFilters = {
      ...filters,
      ...newFilters,
    }

    setFilters({
      ...updatedFilters,
      search,
    })

    const params = new URLSearchParams(searchParams)
    params.delete('newsletters')
    if (!isEmpty(newsletters) && !isEmpty(updatedFilters.newslettersUuids)) {
      updatedFilters.newslettersUuids?.forEach((newsletterUuid) =>
        params.append('newsletters', newsletterUuid),
      )
    }

    params.delete('authors')
    if (!isEmpty(updatedFilters.authorsUuids)) {
      updatedFilters.authorsUuids?.forEach((authorUuid) =>
        params.append('authors', authorUuid),
      )
    }

    router.replace(`${pathname}?${params.toString()}`)
  }

  const handleNewsletterToggle = (newsletterUuid: string) => {
    if (isEmpty(newsletters)) {
      return
    }

    const updatedFilters = {
      ...filters,
      newslettersUuids: filters.newslettersUuids?.includes(newsletterUuid)
        ? filters.newslettersUuids?.filter((uuid) => uuid !== newsletterUuid)
        : [...(filters.newslettersUuids ?? []), newsletterUuid],
    }

    handleFiltersChange(updatedFilters)
  }

  const handleSearchUpdate = (
    newValue: string,
    from: 'button' | 'change' | 'keyboard',
  ) => {
    setSearch(newValue)

    if (newValue === '' || from === 'button' || from === 'keyboard') {
      triggerSearch(newValue, from === 'button' || from === 'keyboard')
    }
  }

  const [count, setCount] = useState(
    initialCount?.[EditionStatuses.Published] ?? 0,
  )
  const [editions, setEditions] = useState<Editions>(initialEditionsList ?? [])
  const [nextPage, setNextPage] = useState(initialNextPage ?? '')

  const initialLoad = useRef(true)
  const [isLoading, setIsLoading] = useState(isEmpty(editions))
  const asyncEditions = useAsync(async () => {
    if (
      !refresh ||
      (!isEmpty(initialEditionsList) &&
        !isEmpty(initialCount) &&
        initialLoad.current)
    ) {
      initialLoad.current = false

      return initialEditionsList
    }

    setIsLoading(true)

    let editionsListResult: Awaited<
      ReturnType<typeof endpoints.editions.list>
    > | null = null

    const limitToUse = limit ?? editionsListPageSize

    try {
      editionsListResult = await endpoints.editions.list(
        filters.search,
        omit(['search'], {
          ...filters,
          mode: PublicationModes.Media,
        }),
        EditionStatuses.Published,
        { limit: limitToUse },
      )

      setCount(editionsListResult.count[EditionStatuses.Published] ?? 0)
      setEditions(editionsListResult.editions ?? [])
      setNextPage(
        !isNumber(limitToUse) || editionsListResult.editions.length < limitToUse
          ? (editionsListResult.nextUrl ?? '')
          : '',
      )
    } catch (error) {
      const actualError = error as Error
      logger.error(
        '[EDITIONS_LIST] An error occurred when searching for the editions list',
        { filters, limit: limitToUse, pathname, searchParams },
        actualError,
      )

      toast.error($t('toast.error.editions.search.unexpected'))
    } finally {
      setIsLoading(constant(false))
    }

    return editionsListResult
  }, [filters, retriggerSearch])

  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false)
  const getNextPage = useCallback(async () => {
    const limitToUse = limit ?? editionsListPageSize

    if (
      isLoadingNextPage ||
      isEmpty(nextPage) ||
      (isNumber(limitToUse) && editions?.length > limitToUse)
    ) {
      return
    }

    setIsLoadingNextPage(true)

    try {
      const editionsListResult = await endpoints.editions.list(
        filters.search,
        omit(['search'], {
          ...filters,
          mode: PublicationModes.Media,
        }),
        EditionStatuses.Published,
        {
          limit: Math.max(editions?.length ?? 0, limitToUse),
          url: nextPage ?? '',
        },
      )

      setEditions((previous) => [
        ...previous,
        ...(editionsListResult.editions ?? []),
      ])
      setNextPage(editionsListResult.nextUrl ?? '')
    } catch (error) {
      const actualError = error as Error
      logger.error(
        '[EDITIONS_LIST] An error occurred when loading the next page of the editions list',
        {
          filters,
          limit: limitToUse,
          pathname,
          searchParams,
          url: nextPage ?? '',
        },
        actualError,
      )

      toast.error($t('toast.error.editions.next_page.unexpected'))
    } finally {
      setIsLoadingNextPage(constant(false))
    }
  }, [
    $t,
    editions?.length,
    editionsListPageSize,
    endpoints.editions,
    filters,
    isLoadingNextPage,
    limit,
    logger,
    nextPage,
    pathname,
    searchParams,
  ])

  const listWrapperClassNames = cn(
    'flex w-full flex-row flex-wrap items-stretch gap-3 sm:gap-5',
    smallPreview ? 'gap-2' : 'gap-5',
    centered ? 'justify-center' : 'justify-between',
    variant === 'list' ? 'max-w-screen-md' : 'max-w-screen-xxl',
    className,
  )

  return (
    <div className="flex w-full flex-col items-center gap-5 md:gap-8">
      {(!isEmpty(newsletters) || withSearch) && (
        <div className="flex w-full flex-col gap-6 md:px-10">
          {!isEmpty(newsletters) && (
            <NewsletterTagsList
              newsletters={newsletters}
              selected={filters.newslettersUuids ?? []}
              onToggle={handleNewsletterToggle}
            >
              <div className="pl-1 xs:pl-2">
                <MobileSearch
                  searchOnClear
                  withUrlParams
                  onSearch={handleSearchUpdate}
                />
              </div>
            </NewsletterTagsList>
          )}

          {withSearch && (
            <SearchInput
              className="mx-auto hidden md:[display:unset]"
              searchOnChange={false}
              searchOnClear
              withUrlParams
              onSearch={handleSearchUpdate}
            />
          )}
        </div>
      )}

      <div className={cj('px-2 sm:px-3', listWrapperClassNames)}>
        <div className="flex flex-col gap-2 sm:gap-4">
          {withCount && (
            <Typography
              className="flex w-fit items-center gap-2"
              element="div"
              variant="heading-3"
            >
              <span>{$t('editions.list.count', { count })}</span>

              {isLoading && <Loader size="s" />}
            </Typography>
          )}

          {!isLoading && asyncEditions.error && (
            <DisplayError error={asyncEditions.error} />
          )}

          {variant === 'bento' ? (
            <>
              <div className="flex flex-col gap-2 sm:gap-4 md:flex-row">
                <EditionCard
                  key={editions[0]!.uuid}
                  className="!size-full max-w-unset [&>div:not(:first-child)]:h-unset"
                  condensed={condensedCards}
                  edition={editions[0]!}
                  orientation="vertical"
                  rootClassName="md:max-w-[464px]"
                  user={user}
                  withNewsletterTag={withNewsletterTag}
                  withPriceTag={withPriceTag}
                />

                <div className="flex flex-col justify-stretch gap-2 sm:gap-4">
                  {editions
                    ?.slice(1, 4)
                    ?.map((edition) => (
                      <EditionCard
                        key={edition.uuid}
                        className="h-full"
                        condensed
                        edition={edition}
                        orientation="horizontal"
                        rootClassName="h-1/3"
                        user={user}
                        withDescription={false}
                        withFooter={false}
                        withNewsletterTag={withNewsletterTag}
                        withPriceTag={withPriceTag}
                      />
                    ))}
                </div>
              </div>

              {editions
                ?.slice(4)
                ?.map((edition) => (
                  <EditionCard
                    key={edition.uuid}
                    condensed={condensedCards}
                    edition={edition}
                    orientation="horizontal"
                    user={user}
                    withNewsletterTag={withNewsletterTag}
                    withPriceTag={withPriceTag}
                  />
                ))}
            </>
          ) : (
            <div
              className={
                variant === 'cards'
                  ? cj(
                      'flex w-full gap-3',
                      isEmpty(orientation) || orientation === 'auto'
                        ? 'flex-col md:flex-row'
                        : '',
                      orientation === 'horizontal' ? 'flex-row' : 'flex-col',
                    )
                  : listWrapperClassNames
              }
            >
              {editions?.map((edition, index) => (
                <EditionCard
                  key={edition.uuid}
                  className={cj(
                    editionCardClassNames,
                    variant === 'cards'
                      ? ((isEmpty(orientation) || orientation === 'auto') &&
                          index === 0 &&
                          mediaQueries.toMd) ||
                        orientation === 'vertical'
                        ? 'h-unset'
                        : ''
                      : index === 0 && mediaQueries.toMd
                        ? 'h-unset'
                        : '',
                  )}
                  condensed={condensedCards}
                  edition={edition}
                  fullWidth={index === 0 && mediaQueries.toMd}
                  orientation={
                    variant === 'cards'
                      ? isEmpty(orientation) || orientation === 'auto'
                        ? index === 0 && mediaQueries.toMd
                          ? 'vertical'
                          : 'horizontal'
                        : orientation === 'horizontal'
                          ? 'vertical'
                          : 'horizontal'
                      : index === 0 && mediaQueries.toMd
                        ? 'vertical'
                        : 'horizontal'
                  }
                  user={user}
                  withNewsletterTag={withNewsletterTag}
                  withPriceTag={withPriceTag}
                />
              ))}
            </div>
          )}
        </div>

        {!isEmpty(nextPage) && (
          <div className="flex w-full justify-center">
            <Button
              disabled={isLoadingNextPage}
              icon={isLoadingNextPage ? <Loader size="xs" /> : <PlusIcon />}
              iconPosition="left"
              label={$t('editions.list.next_page')}
              variant="secondary"
              onClick={getNextPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}
